import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiPost } from '@/lib/api-client';

/**
 * Unified generation request schema
 * Supports both new generation and edit mode via sessionId
 */
export const UnifiedGenerateRequestSchema = z.object({
  prompt: z.string().min(3).max(30000),
  userPrompt: z.string().min(3).max(30000).optional(), // Original user prompt for display/storage
  sessionId: z.string().uuid().optional(),
  // Which version to edit FROM (optional - defaults to session's currentVersion)
  // Use this when user clicks on a specific version in timeline and wants to edit from there
  fromVersionId: z.string().uuid().optional(),
  isEdit: z.boolean().optional(),
  forceNew: z.boolean().optional().default(false),
  projectId: z.string().uuid().optional(),
  preferences: z.object({
    fps: z.number().int().min(24).max(60).optional(),
    width: z.number().int().min(100).max(4096).optional(),
    height: z.number().int().min(100).max(4096).optional(),
    durationInSeconds: z.number().min(1).max(300).optional(),
    audioUrl: z.string().url().optional(),
  }).optional(),
  sessionTitle: z.string().max(255).optional(),
});

export type UnifiedGenerateRequest = z.infer<typeof UnifiedGenerateRequestSchema>;

/**
 * Unified generation response schema
 * Returns version, session, and intent detection info
 */
const UnifiedGenerateResponseSchema = z.object({
  version: z.object({
    id: z.string().uuid(),
    sessionId: z.string().uuid(),
    versionNumber: z.number(),
    parentVersionId: z.string().uuid().nullable(),
    isEdit: z.boolean(),
    prompt: z.string(),
    editInstruction: z.string().nullable(),
    code: z.string(),
    componentName: z.string(),
    explanation: z.string().nullable(),
    preferences: z.object({
      fps: z.number(),
      width: z.number(),
      height: z.number(),
      durationInSeconds: z.number(),
      audioUrl: z.string().optional(),
    }),
    isValid: z.boolean(),
    validationErrors: z.array(z.object({
      type: z.string(),
      message: z.string(),
      line: z.number().optional(),
      column: z.number().optional(),
    })),
    citations: z.array(z.object({
      source: z.string(),
      relevance: z.number(),
      snippet: z.string(),
    })),
    tokensInput: z.number(),
    tokensOutput: z.number(),
    tokensTotal: z.number(),
    generationTimeMs: z.number(),
    modelVersion: z.string(),
    createdAt: z.number(),
  }),
  session: z.object({
    id: z.string().uuid(),
    userId: z.string(),
    projectId: z.string().uuid().nullable(),
    title: z.string().nullable(),
    currentVersionId: z.string().uuid().nullable(),
    versionCount: z.number(),
    totalTokensUsed: z.number(),
    createdAt: z.number(),
    updatedAt: z.number(),
  }),
  detectedIntent: z.enum(['new', 'edit']),
  intentConfidence: z.number(),
  isNewSession: z.boolean(),
});

export type UnifiedGenerateResponse = z.infer<typeof UnifiedGenerateResponseSchema>;

/**
 * Hook for generating Remotion components via unified API
 * Supports both new generation and edit mode
 */
export function useGeneration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UnifiedGenerateRequest) => {
      return apiPost('/generate', data, UnifiedGenerateResponseSchema);
    },
    onSuccess: (response) => {
      // Invalidate projects list
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      // Invalidate usage
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      // Invalidate sessions list (matches sessionKeys.all)
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      // Invalidate specific session timeline if exists (matches sessionKeys.timeline)
      if (response.data?.session?.id) {
        const sessionId = response.data.session.id;
        // Invalidate all session-related queries
        queryClient.invalidateQueries({ queryKey: ['sessions', sessionId] });
      }
    },
  });
}

// ============================================================================
// Backward Compatibility
// ============================================================================

/**
 * @deprecated Use UnifiedGenerateRequestSchema instead
 * Legacy generation request schema for backward compatibility
 */
export const GenerateRequestSchema = z.object({
  prompt: z.string().min(10).max(5000),
  projectId: z.string().uuid().optional(),
  preferences: z.object({
    fps: z.number().int().min(24).max(60).default(30),
    width: z.number().int().min(100).max(4096).default(1920),
    height: z.number().int().min(100).max(4096).default(1080),
    durationInSeconds: z.number().min(1).max(300).default(10),
    audioUrl: z.string().url().optional(),
  }).optional(),
});

/**
 * @deprecated Use UnifiedGenerateRequest instead
 */
export type GenerateRequest = z.infer<typeof GenerateRequestSchema>;

/**
 * Legacy generation data format for backward compatibility
 */
export type GenerationData = {
  code: string;
  componentName: string;
  explanation?: string;
  previewUrl: string | null;
  isValid?: boolean;
  validationErrors?: string[];
  metadata: {
    tokensUsed: number;
    generationTimeMs: number;
    modelVersion: string;
    citations: Array<{
      source: string;
      relevance: number;
      snippet: string;
    }>;
  };
};

/**
 * Helper to convert new unified response to legacy GenerationData format
 * Use this when you need to work with code expecting the old response shape
 */
export function toGenerationData(response: UnifiedGenerateResponse): GenerationData {
  const v = response.version;
  return {
    code: v.code,
    componentName: v.componentName,
    explanation: v.explanation ?? undefined,
    previewUrl: null,
    isValid: v.isValid,
    validationErrors: v.validationErrors.map(e => e.message),
    metadata: {
      tokensUsed: v.tokensTotal,
      generationTimeMs: v.generationTimeMs,
      modelVersion: v.modelVersion,
      citations: v.citations,
    },
  };
}
